/**
 * Real-Time Audio Test Panel
 * Component for testing guitar effects with sample audio
 * Includes visual audio monitoring and latency measurement
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { DropdownPortal } from '../../ui/dropdown-portal';
import {
  Play,
  Pause,
  Square,
  Volume2,
  VolumeX,
  Activity,
  Music,
  Zap,
  RotateCcw,
  ChevronDown,
  Gauge,
  Loader2,
  Guitar,
  CheckCircle,
  AlertCircle,
  AudioWaveform,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Available test samples
const TEST_SAMPLES = [
  { id: 'guitar-riff', name: 'Guitar Riff', description: 'Open string progression', icon: '🎸' },
  { id: 'power-chord', name: 'Power Chord', description: 'Heavy chord for distortion', icon: '🤘' },
  { id: 'tone', name: 'Test Tone', description: 'Pure 440Hz sine wave', icon: '🔊' },
];

// Visual analyser component
const AudioVisualizer = ({ analyserNode, isPlaying }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  
  useEffect(() => {
    if (!analyserNode || !isPlaying) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const bufferLength = analyserNode.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);
      analyserNode.getByteFrequencyData(dataArray);
      
      ctx.fillStyle = 'rgba(24, 24, 27, 0.9)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const barWidth = (canvas.width / bufferLength) * 2.5;
      let x = 0;
      
      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * canvas.height * 0.8;
        
        // Gradient based on frequency
        const hue = (i / bufferLength) * 120 + 200; // Blue to purple
        ctx.fillStyle = `hsla(${hue}, 80%, 55%, 0.9)`;
        
        ctx.fillRect(x, canvas.height - barHeight, barWidth - 1, barHeight);
        x += barWidth;
      }
    };
    
    draw();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [analyserNode, isPlaying]);
  
  return (
    <canvas
      ref={canvasRef}
      width={400}
      height={80}
      className="w-full h-20 rounded-lg bg-zinc-900/50"
    />
  );
};

// Waveform visualizer (time domain)
const WaveformVisualizer = ({ analyserNode, isPlaying }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  
  useEffect(() => {
    if (!analyserNode || !isPlaying) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // Draw flat line when not playing
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#18181b';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = '#3f3f46';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, canvas.height / 2);
        ctx.lineTo(canvas.width, canvas.height / 2);
        ctx.stroke();
      }
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const bufferLength = analyserNode.fftSize;
    const dataArray = new Uint8Array(bufferLength);
    
    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);
      analyserNode.getByteTimeDomainData(dataArray);
      
      ctx.fillStyle = '#18181b';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#a855f7';
      ctx.beginPath();
      
      const sliceWidth = canvas.width / bufferLength;
      let x = 0;
      
      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * canvas.height) / 2;
        
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
        x += sliceWidth;
      }
      
      ctx.lineTo(canvas.width, canvas.height / 2);
      ctx.stroke();
    };
    
    draw();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [analyserNode, isPlaying]);
  
  return (
    <canvas
      ref={canvasRef}
      width={400}
      height={60}
      className="w-full h-14 rounded-lg"
    />
  );
};

// Main Audio Test Panel Component
export const AudioTestPanel = ({ 
  guitarEffectsChain,
  onEffectsStatusChange,
  currentPreset
}) => {
  const [selectedSample, setSelectedSample] = useState(TEST_SAMPLES[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [effectsEnabled, setEffectsEnabled] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [latencyMs, setLatencyMs] = useState(null);
  const [effectsStatus, setEffectsStatus] = useState(null);
  const sampleSelectorRef = useRef(null);
  
  // Audio refs
  const audioContextRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const analyserRef = useRef(null);
  const gainNodeRef = useRef(null);
  const audioBufferRef = useRef(null);
  
  // Initialize audio context
  const initAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create gain node
      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.gain.value = volume / 100;
      
      // Create analyser
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
    }
    return audioContextRef.current;
  }, [volume]);
  
  // Load audio sample
  const loadSample = async (sampleId) => {
    setIsLoading(true);
    try {
      const ctx = initAudioContext();
      
      // Resume context if suspended
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      
      const startTime = performance.now();
      
      // Fetch audio from API
      const response = await fetch(`${API_URL}/api/audio-test/${sampleId}`);
      if (!response.ok) throw new Error('Failed to load audio');
      
      const arrayBuffer = await response.arrayBuffer();
      audioBufferRef.current = await ctx.decodeAudioData(arrayBuffer);
      
      const loadTime = performance.now() - startTime;
      setLatencyMs(Math.round(loadTime));
      
      return true;
    } catch (error) {
      console.error('Error loading sample:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Play audio through effects chain
  const playAudio = async () => {
    if (isPlaying) {
      stopAudio();
      return;
    }
    
    const loaded = await loadSample(selectedSample.id);
    if (!loaded) return;
    
    const ctx = audioContextRef.current;
    
    // Create buffer source
    sourceNodeRef.current = ctx.createBufferSource();
    sourceNodeRef.current.buffer = audioBufferRef.current;
    
    // Connect audio chain
    // Source -> Effects (if enabled) -> Gain -> Analyser -> Destination
    let lastNode = sourceNodeRef.current;
    
    if (effectsEnabled && guitarEffectsChain) {
      try {
        // Connect to effects chain
        lastNode.connect(guitarEffectsChain.input);
        guitarEffectsChain.connect(gainNodeRef.current);
        lastNode = null; // Effects chain handles routing
        
        setEffectsStatus('active');
        if (onEffectsStatusChange) {
          onEffectsStatusChange('active');
        }
      } catch (err) {
        console.error('Could not connect effects chain:', err);
        setEffectsStatus('error');
      }
    } else {
      setEffectsStatus('bypassed');
    }
    
    if (lastNode) {
      lastNode.connect(gainNodeRef.current);
    }
    
    gainNodeRef.current.connect(analyserRef.current);
    analyserRef.current.connect(ctx.destination);
    
    // Start playback
    sourceNodeRef.current.start(0);
    setIsPlaying(true);
    
    // Handle playback end
    sourceNodeRef.current.onended = () => {
      setIsPlaying(false);
      setEffectsStatus(null);
    };
  };
  
  // Stop audio
  const stopAudio = () => {
    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.stop();
        sourceNodeRef.current.disconnect();
      } catch (e) {
        // Already stopped
      }
    }
    setIsPlaying(false);
    setEffectsStatus(null);
  };
  
  // Update volume
  useEffect(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = isMuted ? 0 : volume / 100;
    }
  }, [volume, isMuted]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAudio();
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);
  
  const handleSampleSelect = (sample) => {
    setSelectedSample(sample);
    setShowDropdown(false);
    stopAudio();
  };
  
  return (
    <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Activity className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Audio Test</h3>
            <p className="text-xs text-zinc-500">Test guitar effects in real-time</p>
          </div>
        </div>
        
        {/* Latency indicator */}
        {latencyMs !== null && (
          <div className="flex items-center gap-1.5 text-xs text-zinc-400">
            <Gauge className="w-3.5 h-3.5" />
            <span>{latencyMs}ms</span>
          </div>
        )}
      </div>
      
      {/* Sample Selector */}
      <div className="relative">
        <button
          ref={sampleSelectorRef}
          onClick={() => setShowDropdown(!showDropdown)}
          className="w-full flex items-center justify-between px-4 py-3 bg-zinc-800/50 border border-zinc-700 rounded-lg hover:border-zinc-600 transition-colors"
        >
          <div className="flex items-center gap-3">
            <span className="text-lg">{selectedSample.icon}</span>
            <div className="text-left">
              <p className="text-sm font-medium text-white">{selectedSample.name}</p>
              <p className="text-xs text-zinc-500">{selectedSample.description}</p>
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>
        
        <DropdownPortal
          triggerRef={sampleSelectorRef}
          isOpen={showDropdown}
          onClose={() => setShowDropdown(false)}
          width="trigger"
        >
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl overflow-hidden">
            {TEST_SAMPLES.map((sample) => (
              <button
                key={sample.id}
                onClick={() => handleSampleSelect(sample)}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-700/50 transition-colors ${
                  selectedSample.id === sample.id ? 'bg-violet-500/20' : ''
                }`}
              >
                <span className="text-lg">{sample.icon}</span>
                <div className="text-left">
                  <p className="text-sm font-medium text-white">{sample.name}</p>
                  <p className="text-xs text-zinc-500">{sample.description}</p>
                </div>
              </button>
            ))}
          </div>
        </DropdownPortal>
      </div>
      
      {/* Waveform Display */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-zinc-400">
          <span className="flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5" />
            Waveform
          </span>
          <span className={`flex items-center gap-1 ${
            effectsStatus === 'active' ? 'text-emerald-400' :
            effectsStatus === 'bypassed' ? 'text-amber-400' :
            effectsStatus === 'error' ? 'text-red-400' : ''
          }`}>
            {effectsStatus === 'active' && <><CheckCircle className="w-3 h-3" /> Effects Active</>}
            {effectsStatus === 'bypassed' && <><AlertCircle className="w-3 h-3" /> Bypassed</>}
            {effectsStatus === 'error' && <><AlertCircle className="w-3 h-3" /> Error</>}
          </span>
        </div>
        <WaveformVisualizer analyserNode={analyserRef.current} isPlaying={isPlaying} />
      </div>
      
      {/* Frequency Spectrum */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 text-xs text-zinc-400">
          <Activity className="w-3.5 h-3.5" />
          <span>Frequency Spectrum</span>
        </div>
        <AudioVisualizer analyserNode={analyserRef.current} isPlaying={isPlaying} />
      </div>
      
      {/* Controls */}
      <div className="flex items-center gap-3">
        {/* Play/Stop Button */}
        <Button
          onClick={playAudio}
          disabled={isLoading}
          className={`flex-1 ${isPlaying ? 'bg-red-600 hover:bg-red-500' : 'bg-violet-600 hover:bg-violet-500'}`}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : isPlaying ? (
            <Square className="w-4 h-4 mr-2" />
          ) : (
            <Play className="w-4 h-4 mr-2" />
          )}
          {isLoading ? 'Loading...' : isPlaying ? 'Stop' : 'Play'}
        </Button>
        
        {/* Effects Toggle */}
        <Button
          variant="outline"
          size="icon"
          onClick={() => setEffectsEnabled(!effectsEnabled)}
          className={`${effectsEnabled ? 'border-violet-500 text-violet-400' : 'border-zinc-700 text-zinc-500'}`}
          title={effectsEnabled ? 'Effects On' : 'Effects Off'}
        >
          <Zap className="w-4 h-4" />
        </Button>
        
        {/* Mute Toggle */}
        <Button
          variant="outline"
          size="icon"
          onClick={() => setIsMuted(!isMuted)}
          className="border-zinc-700"
        >
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </Button>
      </div>
      
      {/* Volume Slider */}
      <div className="flex items-center gap-3">
        <Volume2 className="w-4 h-4 text-zinc-400" />
        <input
          type="range"
          min="0"
          max="100"
          value={volume}
          onChange={(e) => setVolume(parseInt(e.target.value))}
          className="flex-1 h-2 bg-zinc-700 rounded-full appearance-none cursor-pointer"
        />
        <span className="text-sm text-zinc-400 w-10 text-right">{volume}%</span>
      </div>
      
      {/* Current Preset Indicator */}
      {currentPreset && (
        <div className="flex items-center gap-2 px-3 py-2 bg-zinc-800/50 rounded-lg">
          <Guitar className="w-4 h-4 text-violet-400" />
          <span className="text-sm text-zinc-400">Testing with:</span>
          <span className="text-sm font-medium text-white">
            {currentPreset.icon} {currentPreset.name}
          </span>
        </div>
      )}
    </div>
  );
};

export default AudioTestPanel;
